import { Divider, Illustration, List, ListItem, Text } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { DeprecatedGlobalAppV1Paths } from "@src/appV2/App/paths";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { STRIPE_ONBOARDING_FULL_PATH } from "@src/appV2/redesign/Payments/Onboarding/paths";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { useShouldBeOnboardedToStripe } from "@src/appV2/Stripe/useShouldBeOnboardedToStripe";
import { useShouldUseNewStripeOnboarding } from "@src/appV2/Stripe/useShouldUseNewStripeOnboarding";
import { useSupportLinks } from "@src/appV2/support/hooks/useSupportLinks";
import { useZendeskArticle } from "@src/appV2/Zendesk/useZendeskArticle";
import { useHistory } from "react-router-dom";

import { useFlexActivationMessaging } from "../hooks/useFlexActivationMessaging";

interface StepNumberProps {
  number: number;
}

function StepNumber(props: StepNumberProps) {
  const { number } = props;

  return (
    <Box
      sx={(theme) => ({
        backgroundColor: theme.palette.info.light,
        width: 24,
        height: 24,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 5,
      })}
    >
      <Text bold variant="body1">
        {number}
      </Text>
    </Box>
  );
}

interface ReferralShareLinkPageContentProps {
  maxReferralBonusAmount: number;
  onShareReferral: () => void;
  isLoading: boolean;
}

export function ReferralShareLinkPageContent(props: ReferralShareLinkPageContentProps) {
  const { maxReferralBonusAmount, onShareReferral, isLoading } = props;

  const history = useHistory();
  const shouldUseNewStripeOnboarding = useShouldUseNewStripeOnboarding();
  const { openArticle } = useZendeskArticle();
  const { workerReferralProgramSupportLink } = useSupportLinks();
  const { getReferralRequirementMessage } = useFlexActivationMessaging();

  const shouldOnboardToStripe = useShouldBeOnboardedToStripe();

  useLogEffect(APP_V2_USER_EVENTS.REFERRAL_SHARE_LINK_PAGE_VIEWED);

  return (
    <PageContent sx={{ py: 5 }}>
      <Stack alignItems="center" gap={5} flex={1}>
        <Illustration type="mail-person" />
        <Text variant="h3" align="center">
          Refer a friend and you both earn ${maxReferralBonusAmount}
        </Text>

        <List component="div">
          <ListItem alignItems="flex-start">
            <StepNumber number={1} />

            <Stack direction="column" sx={{ marginLeft: 6 }} gap={3}>
              <Text bold variant="body1">
                Share your link with your friend
              </Text>

              <Text
                sx={{
                  marginTop: 2,
                }}
              >
                They can use this link to register for the referral. If they haven’t downloaded the
                Clipboard app yet, they will be asked to first install the app and create an
                account.
              </Text>

              <TextButton
                onClick={async () => {
                  await openArticle(workerReferralProgramSupportLink);
                }}
              >
                Learn more
              </TextButton>
            </Stack>
          </ListItem>
          <Divider />
          <ListItem alignItems="flex-start">
            <StepNumber number={2} />

            <Stack direction="column" sx={{ marginLeft: 6 }} gap={3}>
              <Text bold variant="body1">
                Your friend {getReferralRequirementMessage({ useThirdPerson: true })} and then you
                both get ${maxReferralBonusAmount} each.
              </Text>
            </Stack>
          </ListItem>
        </List>
      </Stack>
      {shouldOnboardToStripe && (
        <LoadingButton
          isLoading={false}
          variant="contained"
          sx={{ marginBottom: 2 }}
          onClick={() => {
            logEvent(APP_V2_USER_EVENTS.STARTED_STRIPE_SETUP, {
              source: "referral_share_link_page",
            });
            if (shouldUseNewStripeOnboarding) {
              logEvent(APP_V2_USER_EVENTS.PAYMENTS_ONBOARDING_ENTRYPOINT_CLICKED, {
                sourceUI: "referral-share-link-page",
              });
            }
            history.push(
              shouldUseNewStripeOnboarding
                ? STRIPE_ONBOARDING_FULL_PATH
                : DeprecatedGlobalAppV1Paths.PAYMENT_SERVICE_ONBOARDING,
              {
                showBackButton: true,
              }
            );
          }}
        >
          Set up Stripe first before sharing your referral link!
        </LoadingButton>
      )}
      <LoadingButton
        isLoading={isLoading}
        disabled={shouldOnboardToStripe}
        startIconType="share"
        onClick={onShareReferral}
      >
        Share your link
      </LoadingButton>
    </PageContent>
  );
}
